import { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Progress } from '../../components/ui/progress';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { Heart, Target, CalendarDays, MessageSquare, BookOpen, Phone, TrendingUp, Smile, Briefcase, Route, Star, CheckCircle, Video } from 'lucide-react';
import { formatDate } from '../../lib/utils';
import ParticipantActionCenter from '../../components/ParticipantActionCenter';

export default function ClientDashboard() {
  const { user, axiosAuth } = useAuth();
  const [checkins, setCheckins] = useState([]);
  const [goals, setGoals] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [journey, setJourney] = useState(null);
  const [roadmap, setRoadmap] = useState(null);
  const [peerAvailability, setPeerAvailability] = useState([]);
  const [showEmergency, setShowEmergency] = useState(false);
  const navigate = useNavigate();
  const api = axiosAuth();

  useEffect(() => {
    api.get('/checkins').then(r => setCheckins(r.data)).catch(() => {});
    api.get('/goals').then(r => setGoals(r.data)).catch(() => {});
    api.get('/appointments').then(r => setAppointments(r.data)).catch(() => {});
    api.get('/journey/progress').then(r => setJourney(r.data)).catch(() => {});
    api.get('/roadmap/progress').then(r => setRoadmap(r.data)).catch(() => {});
    api.get('/availability/peers').then(r => setPeerAvailability(r.data)).catch(() => {});
  }, []);

  const lastCheckin = checkins[0];
  const activeGoals = goals.filter(g => g.status === 'active');
  const upcomingAppts = appointments.filter(a => a.status === 'scheduled').slice(0, 3);
  const avgMood = checkins.length > 0 ? (checkins.slice(0, 7).reduce((s, c) => s + c.mood, 0) / Math.min(checkins.length, 7)).toFixed(1) : '—';
  const soberDays = checkins.filter(c => c.stayed_sober).length;
  const workDays = checkins.filter(c => c.attended_work).length;
  const roadmapCompleted = roadmap?.milestones?.filter(m => m.completed)?.length || 0;
  const availablePeers = peerAvailability.filter(p => p.available);

  return (
    <div className="space-y-6" data-testid="client-dashboard">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-foreground" style={{ fontFamily: 'Manrope' }}>
            Welcome back, {user.full_name?.split(' ')[0]}
          </h1>
          <p className="text-muted-foreground mt-1 text-sm md:text-base">Here's your recovery journey overview</p>
        </div>
        <Button onClick={() => navigate('/checkin')} className="bg-teal-600 hover:bg-teal-700 text-white rounded-full px-6 gap-2 shrink-0" data-testid="daily-checkin-button">
          <Heart size={16} /> Daily Check-in
        </Button>
      </div>

      {/* Crisis Button */}
      <div className="bg-red-50 border border-red-200 rounded-2xl p-4 flex flex-col md:flex-row md:items-center justify-between gap-3">
        <div>
          <p className="font-semibold text-red-800 text-sm">Need immediate support?</p>
          <p className="text-red-600 text-xs">24/7 Crisis Line: 988 Suicide & Crisis Lifeline</p>
          <p className="text-red-600 text-xs">Online: 988lifeline.org</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="destructive" className="rounded-full gap-2 shrink-0" data-testid="crisis-support-button" onClick={() => window.open('tel:988')}>
            <Phone size={16} /> Call 988
          </Button>
          <Button variant="outline" className="rounded-full" data-testid="crisis-support-link" onClick={() => window.open('https://988lifeline.org')}>
            Visit Lifeline
          </Button>
        </div>
      </div>

      <Card className="border-0 shadow-sm rounded-2xl bg-white" data-testid="emergency-peer-support">
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-semibold flex items-center gap-2">
            <Heart className="w-4 h-4 text-rose-600" /> Peer Support Now
          </CardTitle>
          <p className="text-xs text-muted-foreground">Connect with a peer specialist who is currently available.</p>
        </CardHeader>
        <CardContent className="flex flex-col md:flex-row md:items-center justify-between gap-3">
          <div className="flex flex-wrap gap-2" data-testid="peer-support-availability">
            {availablePeers.length ? availablePeers.map(peer => (
              <Badge key={peer.id} variant="secondary" className="bg-emerald-50 text-emerald-700" data-testid={`peer-available-${peer.id}`}>
                {peer.full_name || 'Peer Support'}
              </Badge>
            )) : (
              <span className="text-xs text-muted-foreground" data-testid="peer-support-none">No peer supports are marked available right now.</span>
            )}
          </div>
          <Button className="rounded-full bg-rose-600 hover:bg-rose-700 text-white" onClick={() => setShowEmergency(true)} data-testid="peer-support-connect">
            Emergency Peer Support
          </Button>
        </CardContent>
      </Card>

      {/* Stats Grid — All clickable */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
        <Card className="border-0 shadow-sm bg-white rounded-2xl cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate('/checkin')} data-testid="stat-mood">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-8 h-8 rounded-lg bg-teal-50 flex items-center justify-center"><Smile className="w-4 h-4 text-teal-600" /></div>
            </div>
            <p className="text-2xl font-bold text-foreground">{avgMood}</p>
            <p className="text-xs text-muted-foreground">Avg Mood (7d)</p>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm bg-white rounded-2xl cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate('/checkin')} data-testid="stat-checkins">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-8 h-8 rounded-lg bg-sky-50 flex items-center justify-center"><TrendingUp className="w-4 h-4 text-sky-600" /></div>
            </div>
            <p className="text-2xl font-bold text-foreground">{checkins.length}</p>
            <p className="text-xs text-muted-foreground">Total Check-ins</p>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm bg-white rounded-2xl cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate('/goals')} data-testid="stat-goals">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-8 h-8 rounded-lg bg-amber-50 flex items-center justify-center"><Target className="w-4 h-4 text-amber-600" /></div>
            </div>
            <p className="text-2xl font-bold text-foreground">{activeGoals.length}</p>
            <p className="text-xs text-muted-foreground">Active Goals</p>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm bg-white rounded-2xl cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate('/appointments')} data-testid="stat-appts">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-8 h-8 rounded-lg bg-violet-50 flex items-center justify-center"><CalendarDays className="w-4 h-4 text-violet-600" /></div>
            </div>
            <p className="text-2xl font-bold text-foreground">{upcomingAppts.length}</p>
            <p className="text-xs text-muted-foreground">Upcoming Appts</p>
          </CardContent>
        </Card>
      </div>

      {/* Secondary Stats Row — clickable */}
      <div className="grid grid-cols-3 gap-3">
        <Card className="border-0 shadow-sm bg-white rounded-2xl cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate('/checkin')} data-testid="stat-sober">
          <CardContent className="p-4 text-center">
            <div className="w-8 h-8 rounded-lg bg-emerald-50 flex items-center justify-center mx-auto mb-2"><CheckCircle className="w-4 h-4 text-emerald-600" /></div>
            <p className="text-2xl font-bold text-emerald-700">{soberDays}</p>
            <p className="text-xs text-muted-foreground">Sober Days</p>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm bg-white rounded-2xl cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate('/roadmap')} data-testid="stat-roadmap">
          <CardContent className="p-4 text-center">
            <div className="w-8 h-8 rounded-lg bg-indigo-50 flex items-center justify-center mx-auto mb-2"><Route className="w-4 h-4 text-indigo-600" /></div>
            <p className="text-2xl font-bold text-indigo-700">{roadmapCompleted}</p>
            <p className="text-xs text-muted-foreground">Roadmap Steps</p>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm bg-white rounded-2xl cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate('/employment')} data-testid="stat-work">
          <CardContent className="p-4 text-center">
            <div className="w-8 h-8 rounded-lg bg-sky-50 flex items-center justify-center mx-auto mb-2"><Briefcase className="w-4 h-4 text-sky-600" /></div>
            <p className="text-2xl font-bold text-sky-700">{workDays}</p>
            <p className="text-xs text-muted-foreground">Work Days</p>
          </CardContent>
        </Card>
      </div>

      {/* Your Progress Story */}
      <Card className="border-0 shadow-sm rounded-2xl bg-gradient-to-r from-teal-50 to-emerald-50" data-testid="progress-story">
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-semibold flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
            <Star className="w-4 h-4 text-amber-500" /> Your Progress Story
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-teal-800 leading-relaxed">
            {(() => {
              const parts = [];
              if (checkins.length > 0) parts.push(`You've completed ${checkins.length} check-in${checkins.length !== 1 ? 's' : ''}`);
              if (soberDays > 0) parts.push(`reported ${soberDays} sober day${soberDays !== 1 ? 's' : ''}`);
              if (workDays > 0) parts.push(`attended work ${workDays} time${workDays !== 1 ? 's' : ''}`);
              const jobApps = checkins.filter(c => c.applied_for_jobs).length;
              if (jobApps > 0) parts.push(`applied for jobs ${jobApps} time${jobApps !== 1 ? 's' : ''}`);
              if (roadmapCompleted > 0) parts.push(`completed ${roadmapCompleted} roadmap step${roadmapCompleted !== 1 ? 's' : ''}`);
              const milestones = journey?.total_completed || 0;
              if (milestones > 0) parts.push(`achieved ${milestones} life milestone${milestones !== 1 ? 's' : ''}`);
              if (activeGoals.length > 0) parts.push(`are working on ${activeGoals.length} goal${activeGoals.length !== 1 ? 's' : ''}`);
              if (parts.length === 0) return "Your journey is just beginning. Every step matters — start by completing your first daily check-in!";
              return parts.join(', ') + '. Every step you take shows your strength and commitment to growth.';
            })()}
          </p>
        </CardContent>
      </Card>

      {/* Your Next Steps - Action Center */}
      <ParticipantActionCenter axiosAuth={axiosAuth} />

      <div className="grid md:grid-cols-2 gap-4 md:gap-6">
        {/* Recent Check-in */}
        <Card className="border-0 shadow-sm rounded-2xl cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate('/checkin')}>
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-semibold" style={{ fontFamily: 'Manrope' }}>Latest Check-in</CardTitle>
          </CardHeader>
          <CardContent>
            {lastCheckin ? (
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Mood</span>
                  <div className="flex items-center gap-2">
                    <Progress value={lastCheckin.mood * 10} className="w-24 h-2" />
                    <span className="text-sm font-medium">{lastCheckin.mood}/10</span>
                  </div>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Cravings</span>
                  <div className="flex items-center gap-2">
                    <Progress value={lastCheckin.cravings * 10} className="w-24 h-2" />
                    <span className="text-sm font-medium">{lastCheckin.cravings}/10</span>
                  </div>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Goals Met</span>
                  <Badge variant={lastCheckin.goals_met ? "default" : "secondary"} className={lastCheckin.goals_met ? "bg-emerald-100 text-emerald-700" : ""}>
                    {lastCheckin.goals_met ? 'Yes' : 'Not yet'}
                  </Badge>
                </div>
                {lastCheckin.notes && <p className="text-sm text-muted-foreground bg-muted/50 rounded-lg p-2">{lastCheckin.notes}</p>}
                <p className="text-xs text-muted-foreground">{formatDate(lastCheckin.date)}</p>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">No check-ins yet. Start your first one!</p>
            )}
          </CardContent>
        </Card>

        {/* Goals */}
        <Card className="border-0 shadow-sm rounded-2xl">
          <CardHeader className="pb-3 flex flex-row items-center justify-between">
            <CardTitle className="text-base font-semibold" style={{ fontFamily: 'Manrope' }}>My Goals</CardTitle>
            <Button variant="ghost" size="sm" onClick={() => navigate('/goals')} className="text-teal-600 text-xs" data-testid="view-all-goals">View all</Button>
          </CardHeader>
          <CardContent>
            {activeGoals.length > 0 ? (
              <div className="space-y-3">
                {activeGoals.slice(0, 3).map(g => (
                  <div key={g.id} className="space-y-1.5 cursor-pointer hover:opacity-80" onClick={() => navigate('/goals')}>
                    <div className="flex justify-between items-center">
                      <p className="text-sm font-medium">{g.title}</p>
                      <span className="text-xs text-muted-foreground">{g.progress || 0}%</span>
                    </div>
                    <Progress value={g.progress || 0} className="h-1.5" />
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">No active goals. Set your first goal!</p>
            )}
          </CardContent>
        </Card>

        {/* Upcoming Appointments */}
        <Card className="border-0 shadow-sm rounded-2xl">
          <CardHeader className="pb-3 flex flex-row items-center justify-between">
            <CardTitle className="text-base font-semibold" style={{ fontFamily: 'Manrope' }}>Upcoming Appointments</CardTitle>
            <Button variant="ghost" size="sm" onClick={() => navigate('/appointments')} className="text-teal-600 text-xs">View all</Button>
          </CardHeader>
          <CardContent>
            {upcomingAppts.length > 0 ? (
              <div className="space-y-3">
                {upcomingAppts.map(a => (
                  <div key={a.id} className="flex items-center gap-3 p-2.5 rounded-xl bg-muted/30 cursor-pointer hover:bg-muted/50" onClick={() => navigate('/appointments')}>
                    <div className="w-10 h-10 rounded-lg bg-teal-50 flex items-center justify-center shrink-0">
                      <CalendarDays className="w-5 h-5 text-teal-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium capitalize">{a.type} Session</p>
                      <p className="text-xs text-muted-foreground">{a.date} at {a.time}</p>
                    </div>
                    <Badge className="bg-teal-50 text-teal-700 text-xs">{a.status}</Badge>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">No upcoming appointments.</p>
            )}
          </CardContent>
        </Card>

        {/* Quick Actions */}
        <Card className="border-0 shadow-sm rounded-2xl">
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-semibold" style={{ fontFamily: 'Manrope' }}>Quick Actions</CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-2 gap-2">
            <Button variant="outline" className="h-auto py-3 flex-col gap-1.5 rounded-xl hover:bg-teal-50 hover:border-teal-200" onClick={() => navigate('/messages')} data-testid="quick-messages">
              <MessageSquare size={18} className="text-teal-600" />
              <span className="text-xs">Messages</span>
            </Button>
            <Button variant="outline" className="h-auto py-3 flex-col gap-1.5 rounded-xl hover:bg-sky-50 hover:border-sky-200" onClick={() => navigate('/resources')} data-testid="quick-resources">
              <BookOpen size={18} className="text-sky-600" />
              <span className="text-xs">Resources</span>
            </Button>
            <Button variant="outline" className="h-auto py-3 flex-col gap-1.5 rounded-xl hover:bg-amber-50 hover:border-amber-200" onClick={() => navigate('/documents')} data-testid="quick-documents">
              <Target size={18} className="text-amber-600" />
              <span className="text-xs">Documents</span>
            </Button>
            <Button variant="outline" className="h-auto py-3 flex-col gap-1.5 rounded-xl hover:bg-violet-50 hover:border-violet-200" onClick={() => navigate('/appointments')} data-testid="quick-appointments">
              <CalendarDays size={18} className="text-violet-600" />
              <span className="text-xs">Book Appt</span>
            </Button>
          </CardContent>
        </Card>
      </div>

      <Dialog open={showEmergency} onOpenChange={setShowEmergency}>
        <DialogContent className="max-w-3xl" data-testid="peer-support-dialog">
          <DialogHeader>
            <DialogTitle>Emergency Peer Support</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">Choose a peer specialist who is currently available. Live messaging/voice/video will activate once your provider is connected.</p>
            <div className="grid md:grid-cols-2 gap-3">
              {(availablePeers.length ? availablePeers : peerAvailability).map(peer => (
                <div key={peer.id} className="border border-slate-200 rounded-xl p-3 flex flex-col gap-2" data-testid={`peer-support-card-${peer.id}`}>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-semibold text-sm">{peer.full_name || 'Peer Support'}</p>
                      <p className="text-xs text-muted-foreground">{peer.available ? 'Available now' : 'Offline'}</p>
                    </div>
                    <Badge className={peer.available ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-600'}>{peer.available ? 'Online' : 'Offline'}</Badge>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Button size="sm" className="rounded-full" disabled={!peer.available} onClick={() => toast.info('Live texting will activate once Twilio is connected.')} data-testid={`peer-text-${peer.id}`}>
                      <MessageSquare size={14} className="mr-1" /> Text
                    </Button>
                    <Button size="sm" variant="outline" className="rounded-full" disabled={!peer.available} onClick={() => toast.info('Live calling will activate once Twilio is connected.')} data-testid={`peer-call-${peer.id}`}>
                      <Phone size={14} className="mr-1" /> Call
                    </Button>
                    <Button size="sm" variant="outline" className="rounded-full" disabled={!peer.available} onClick={() => toast.info('Video sessions will activate once Twilio is connected.')} data-testid={`peer-video-${peer.id}`}>
                      <Video size={14} className="mr-1" /> Video
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
